import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO/SEO';
import { BreadcrumbSchema } from '../components/SEO/StructuredData';
import './Gallery.css';

function Gallery(): JSX.Element {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);

  // Gallery data with translation keys
  const galleryImages = [
    // CCTV Systems
    { id: 1, src: '/assets/images/services/it/CCTV1.jpg', titleKey: 'galleryPage.images.cctvInstallation', categoryKey: 'galleryPage.categories.cctvSystems', descriptionKey: 'galleryPage.descriptions.highResolutionCameras' },
    { id: 2, src: '/assets/images/services/it/ENTRANCE MANAGEMENT.jpg', titleKey: 'galleryPage.images.accessControl', categoryKey: 'galleryPage.categories.cctvSystems', descriptionKey: 'galleryPage.descriptions.biometricAccess' },
    { id: 3, src: '/assets/images/services/it/CCTV2.jpg', titleKey: 'galleryPage.images.outdoorCCTV', categoryKey: 'galleryPage.categories.cctvSystems', descriptionKey: 'galleryPage.descriptions.weatherResistant' },
    { id: 4, src: '/assets/images/services/it/INTRUSTION ALARMS.jpg', titleKey: 'galleryPage.images.intrusionAlarms', categoryKey: 'galleryPage.categories.cctvSystems', descriptionKey: 'galleryPage.descriptions.advancedAlarms' },

    // Fire Safety
    { id: 5, src: '/assets/images/services/fire/FIRE DETECTION AND ALARM SYSTEMS.jpg', titleKey: 'galleryPage.images.fireDetection', categoryKey: 'galleryPage.categories.fireSafety', descriptionKey: 'galleryPage.descriptions.detectionSystems' },
    { id: 6, src: '/assets/images/services/fire/FIRE SUPPRESSION SYSTEMS.jpg', titleKey: 'galleryPage.images.fireSuppression', categoryKey: 'galleryPage.categories.fireSafety', descriptionKey: 'galleryPage.descriptions.suppressionSystems' },
    { id: 7, src: '/assets/images/services/fire/FIRE HYDRANT AND SPRINKLER SYSTEMS.jpg', titleKey: 'galleryPage.images.fireHydrant', categoryKey: 'galleryPage.categories.fireSafety', descriptionKey: 'galleryPage.descriptions.hydrantSprinkler' },
    { id: 8, src: '/assets/images/services/fire/EXTINGUISHERS.jpg', titleKey: 'galleryPage.images.fireExtinguishers', categoryKey: 'galleryPage.categories.fireSafety', descriptionKey: 'galleryPage.descriptions.extinguisherSystems' },



    // HVAC Systems
    { id: 13, src: '/assets/images/services/hvac/AC SPLIT.jpg', titleKey: 'galleryPage.images.acSystems', categoryKey: 'galleryPage.categories.hvacSystems', descriptionKey: 'galleryPage.descriptions.airConditioning' },
    { id: 14, src: '/assets/images/services/hvac/VRF .jpg', titleKey: 'galleryPage.images.vrfSystems', categoryKey: 'galleryPage.categories.hvacSystems', descriptionKey: 'galleryPage.descriptions.variableRefrigerant' },
    { id: 15, src: '/assets/images/services/hvac/AC MAINTENANCE.jpg', titleKey: 'galleryPage.images.hvacMaintenance', categoryKey: 'galleryPage.categories.hvacSystems', descriptionKey: 'galleryPage.descriptions.professionalMaintenance' },
    { id: 16, src: '/assets/images/services/maintenance/Ac .jpg', titleKey: 'galleryPage.images.acService', categoryKey: 'galleryPage.categories.hvacSystems', descriptionKey: 'galleryPage.descriptions.comprehensiveService' }
  ];

  const categories = [
    { key: 'All', labelKey: 'gallery.all' },
    { key: 'galleryPage.categories.cctvSystems', labelKey: 'gallery.cctvSystems' },
    { key: 'galleryPage.categories.fireSafety', labelKey: 'gallery.fireSafety' },

    { key: 'galleryPage.categories.hvacSystems', labelKey: 'gallery.hvacSystems' }
  ];

  const filteredImages = selectedCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.categoryKey === selectedCategory);

  return (
    <main className="page gallery-page">
      <SEO title="Gallery" description="View Cool & Care's project gallery showcasing CCTV installations, fire safety systems, HVAC solutions, and IT infrastructure projects in DRC." keywords="project gallery, CCTV installation photos, fire safety projects, HVAC installation DRC" />
      <BreadcrumbSchema items={[{ name: 'Gallery', url: '/gallery' }]} />
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">{t('gallery.title')}</h1>
          <p className="page-description">
            {t('gallery.subtitle')}
          </p>
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb-link">{t('common.home')}</Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">{t('gallery.breadcrumb')}</span>
          </nav>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Filter Buttons */}
          <div className="gallery-filters">
            <h3 className="filters-title">{t('gallery.filterByCategory')}</h3>
            <div className="filter-buttons">
              {categories.map((category) => (
                <button
                  key={category.key}
                  className={`filter-btn ${selectedCategory === category.key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.key)}
                >
                  {t(category.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Image Gallery Grid */}
          <div className="image-gallery">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className={`gallery-item ${hoveredImage === image.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredImage(image.id)}
                onMouseLeave={() => setHoveredImage(null)}
              >
                <div className="image-container">
                  <img
                    src={image.src}
                    alt={t(image.titleKey)}
                    className="gallery-image"
                    onError={(e) => {
                      console.warn('Image failed to load:', image.src);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <h3 className="image-title">{t(image.titleKey)}</h3>
                      <p className="image-description">{t(image.descriptionKey)}</p>
                      <span className="image-category">{t(image.categoryKey)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="gallery-cta">
            <h3 className="cta-title">{t('gallery.cta.title')}</h3>
            <p className="cta-description">
              {t('gallery.cta.description')}
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                {t('gallery.cta.freeConsultation')}
              </Link>
              <Link to="/services" className="btn btn-secondary">
                {t('gallery.cta.viewServices')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Gallery;